import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useField, Field } from 'formik';
import { Tooltip } from '..';
import { Change } from '../../models';
import { Icon } from '..';
import type { IconType } from '..';

interface TypeFieldProps {
    name: string;
    readOnly?: boolean;
    defaultValue?: string;
    className?: string;
    /** Current typeDomain value from the change (for non-dev highlight) */
    typeDomain?: string;
    /** Current typeSubtype value from the change (for non-dev highlight) */
    typeSubtype?: string;
    /** Save a single field (legacy) */
    onSave?: (field: string, value: string) => void;
    /** Save type + typeDomain + typeSubtype in one call */
    onSaveMultiple?: (fields: Record<string, string>) => void;
};

const TYPE_DOMAIN_DEV     = 'dev';
const TYPE_DOMAIN_NON_DEV = 'non_dev';

const NON_DEV_SUBTYPES: Array<{ subtype: string; icon: IconType; labelKey: string }> = [
    { subtype: 'support', icon: 'ticket',  labelKey: 'changes.subtype.support' },
    { subtype: 'sales',   icon: 'bill',    labelKey: 'changes.subtype.sales'   },
    { subtype: 'oncall',  icon: 'timer',   labelKey: 'changes.subtype.oncall'  },
    { subtype: 'abuse',   icon: 'warning', labelKey: 'changes.subtype.abuse'   },
    { subtype: 'ops',     icon: 'setting', labelKey: 'changes.subtype.ops'     },
    { subtype: 'other',   icon: 'dots',    labelKey: 'changes.subtype.other'   },
];

// Dev types to display (all except TYPE_NONE, which is superseded by the non-dev row)
const DEV_TYPES = Change.types().filter(t => t !== Change.TYPE_NONE);

const TypeField = (props: TypeFieldProps) => {
    const { name, readOnly, defaultValue, className, typeDomain, typeSubtype, onSave, onSaveMultiple } = props;

    const { t } = useTranslation();

    const [field, meta, helpers]     = useField(name);
    const { setValue }               = helpers;
    const { value, touched, error }  = meta;

    useEffect(() => {
        if (defaultValue)
            setValue(defaultValue);
    }, [defaultValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const saveDevType = (type: string) => {
        if (readOnly) return;
        if (onSaveMultiple)
            return onSaveMultiple({ type, typeDomain: TYPE_DOMAIN_DEV, typeSubtype: type });
        if (onSave)
            return onSave(name, type);
        setValue(type);
    };

    const saveNonDevSubtype = (subtype: string) => {
        if (readOnly) return;
        if (onSaveMultiple)
            return onSaveMultiple({ type: Change.TYPE_NONE, typeDomain: TYPE_DOMAIN_NON_DEV, typeSubtype: subtype });
        if (onSave)
            return onSave(name, Change.TYPE_NONE);
        setValue(Change.TYPE_NONE);
    };

    const isDevActive = (type: string): boolean => {
        // Domain explicitly set to non_dev → no dev type is active
        if (typeDomain === TYPE_DOMAIN_NON_DEV) return false;
        return value === type;
    };

    const isNonDevActive = (subtype: string): boolean => {
        if (typeDomain === TYPE_DOMAIN_NON_DEV && typeSubtype === subtype) return true;
        // Legacy: type=none and no domain/subtype set → try to match by subtype name
        if (!typeDomain && value === Change.TYPE_NONE && typeSubtype === subtype) return true;
        return false;
    };

    const devAvatar = (type: string) => {
        const active = isDevActive(type);
        let cls = 'w-7 h-7 select-none text-center rounded-lg text-xs border ring ring-offset-2 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:ring-2 hover:ring-blue-100 flex items-center justify-center';
        cls += active
            ? ' bg-blue-100 dark:bg-blue-900/40 ring-2'
            : ' bg-gray-100 dark:bg-gray-800 ring-1 ring-gray-300 dark:ring-gray-600';
        if (!readOnly) cls += ' cursor-pointer';

        return (
            <Tooltip key={type} content={t(`projects.${type}`) || type}>
                <div onClick={() => saveDevType(type)} className={cls}>
                    <Icon type={Change.iconName(type)} size={3} />
                </div>
            </Tooltip>
        );
    };

    const nonDevAvatar = ({ subtype, icon, labelKey }: typeof NON_DEV_SUBTYPES[0]) => {
        const active = isNonDevActive(subtype);
        let cls = 'w-7 h-7 select-none text-center rounded-lg text-xs border ring ring-offset-2 hover:bg-green-100 dark:hover:bg-green-900/40 hover:ring-2 hover:ring-green-100 flex items-center justify-center';
        cls += active
            ? ' bg-green-100 dark:bg-green-900/40 ring-2'
            : ' bg-gray-100 dark:bg-gray-800 ring-1 ring-gray-300 dark:ring-gray-600';
        if (!readOnly) cls += ' cursor-pointer';

        return (
            <Tooltip key={subtype} content={t(labelKey) || subtype}>
                <div onClick={() => saveNonDevSubtype(subtype)} className={cls}>
                    <Icon type={icon} size={3} />
                </div>
            </Tooltip>
        );
    };

    return (
        <>
            <div className={`flex flex-col gap-y-2 ${className || ''}`}>
                {/* Dev types row */}
                <div className="flex flex-wrap gap-2">
                    {DEV_TYPES.map(type => (
                        <div key={type}>
                            {devAvatar(type)}
                        </div>
                    ))}
                </div>
                {/* Non-dev subtypes row */}
                <div className="flex flex-wrap gap-2 border-t border-gray-200 dark:border-gray-700 pt-2">
                    {NON_DEV_SUBTYPES.map(item => (
                        <div key={item.subtype}>
                            {nonDevAvatar(item)}
                        </div>
                    ))}
                </div>
            </div>
            <Field as="input" type="hidden" name={name} />
            {touched && error ? (
                <div className="error">{error}</div>
            ) : null}
        </>
    );
};

export default TypeField;
